// CooldownModal — Loss streak cooldown with countdown timer
import React, { useState, useEffect } from 'react';
import { useMind } from '../../../context/MindContext';
import { FaPause, FaTimes } from 'react-icons/fa';

export default function CooldownModal() {
  const { showCooldown, setShowCooldown, cooldownRemaining, overrideCooldown, performance } = useMind();
  const [overrideTimer, setOverrideTimer] = useState(5);
  const [canOverride, setCanOverride] = useState(false);

  // Format remaining ms to mm:ss
  const minutes = Math.floor(cooldownRemaining / 60000);
  const seconds = Math.floor((cooldownRemaining % 60000) / 1000);
  const timeStr = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

  // 5-second delay before override button activates
  useEffect(() => {
    if (!showCooldown) return;
    setOverrideTimer(5);
    setCanOverride(false);

    const interval = setInterval(() => {
      setOverrideTimer(prev => {
        if (prev <= 1) {
          setCanOverride(true);
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showCooldown]);

  if (!showCooldown) return null;

  return (
    <div className="fixed inset-0 z-[210] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
              <FaPause className="text-orange-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Cooldown Active</h2>
              <p className="text-[11px] text-gray-500">
                {performance?.consecutiveLosses || 0} consecutive losses
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowCooldown(false)}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-6 text-center space-y-4">
          {/* Countdown */}
          <div className="py-6">
            <div className="text-4xl font-mono font-bold text-gray-900">{timeStr}</div>
            <p className="text-xs text-gray-500 mt-2">remaining</p>
          </div>

          <p className="text-xs text-gray-600">
            Take a break. Review your trades. Come back with a clear mind.
          </p>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setShowCooldown(false)}
              className="flex-1 py-2.5 rounded-lg text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              Dismiss
            </button>
            <button
              onClick={canOverride ? overrideCooldown : undefined}
              disabled={!canOverride}
              className={`flex-1 py-2.5 rounded-lg text-xs font-bold transition-colors ${
                canOverride
                  ? 'bg-orange-500 text-white hover:bg-orange-600 cursor-pointer'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {canOverride ? 'Override Cooldown' : `Wait ${overrideTimer}s...`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
